import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../../store/useStore";
import Loader from "../../../components/Loader";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

const WishlistSection = () => {
  const {
    wishlist,
    wishlist_loading,
    fetchWishlist,
    removeFromWishlist,
    clearWishlist,
    addToCart,
    isAuthenticated,
    openAuthModal,
  } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchWishlist();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddToCart = async (item) => {
    const errorBefore = useStore.getState().cart_error;
    await addToCart(item, 1);
    const errorAfter = useStore.getState().cart_error;
    if (!errorAfter || errorAfter === errorBefore) {
      toast.success("Added to cart");
    }
  };

  const handleRemove = async (item) => {
    await removeFromWishlist(item.product_id);
    toast.success("Removed from wishlist");
  };

  const handleClearAll = async () => {
    if (window.confirm("Clear all items from your wishlist?")) {
      await clearWishlist();
    }
  };

  if (!isAuthenticated) {
    return (
      <div>
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-lg font-semibold text-gray-900">My List</h1>
          <p className="mt-1 text-sm text-gray-500">
            Sign in to view saved products.
          </p>
        </div>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 py-12 text-center">
          <FavoriteBorderIcon className="text-gray-300" style={{ fontSize: 36 }} />
          <p className="mt-2 text-sm font-semibold text-gray-700">
            Please log in to view your wishlist
          </p>
          <button
            onClick={openAuthModal}
            className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Login / Sign Up
          </button>
        </div>
      </div>
    );
  }

  if (wishlist_loading && wishlist.length === 0) {
    return <Loader compact label="Loading wishlist" />;
  }

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">My List</h1>
          <p className="mt-1 text-sm text-gray-500">
            {wishlist.length} {wishlist.length === 1 ? "item" : "items"} saved.
          </p>
        </div>
        {wishlist.length > 0 && (
          <button
            onClick={handleClearAll}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            <DeleteOutlineOutlinedIcon style={{ fontSize: 17 }} />
            Clear All
          </button>
        )}
      </div>

      {wishlist.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 py-12 text-center">
          <FavoriteBorderIcon className="text-gray-300" style={{ fontSize: 36 }} />
          <p className="mt-2 text-sm font-semibold text-gray-700">
            Your wishlist is empty
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Save products to find them here later.
          </p>
          <button
            onClick={() => navigate("/products")}
            className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Explore Products
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {wishlist.map((item) => {
            const hasDiscount = Number(item.originalPrice) > Number(item.price);
            return (
              <div
                key={item.product_id ?? item.wishlist_id}
                className="flex gap-4 rounded-lg border border-gray-200 p-4"
              >
                <button
                  onClick={() => navigate(`/product/${item.product_id}`)}
                  className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <FavoriteBorderIcon className="text-gray-300" />
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  {item.category && (
                    <p className="text-xs font-medium uppercase text-gray-500">
                      {item.category}
                    </p>
                  )}
                  <button
                    onClick={() => navigate(`/product/${item.product_id}`)}
                    className="mt-1 block max-w-full break-words text-left text-sm font-semibold text-gray-900 hover:text-primary-700"
                  >
                    {item.name}
                  </button>

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-900">
                      Rs {item.price}
                    </span>
                    {hasDiscount && (
                      <span className="text-gray-400 line-through">
                        Rs {item.originalPrice}
                      </span>
                    )}
                    {item.unit && <span className="text-gray-500">/{item.unit}</span>}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleAddToCart(item)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-700"
                    >
                      <ShoppingCartOutlinedIcon style={{ fontSize: 15 }} />
                      Add to Cart
                    </button>
                    <button
                      onClick={() => handleRemove(item)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WishlistSection;
